import { useGalleryStore } from 'src/domain/stores/useGalleryStore';
import type { ApiGallery, ApiGalleryChild } from 'src/core/models/api/ApiGallery';
import type { ApiPageGallery } from 'src/core/models/api/ApiPage';
import type {
	RootGalleryHolderScreenParams,
	RootGalleryScreenParams,
} from 'src/presentation/navigation/types/NavigationTypes';

type GalleryPage = ApiGalleryChild | ApiPageGallery;

function asId(value: string | number | undefined): string | undefined {
	return value === undefined ? undefined : String(value);
}

/**
 * The photo album a route points at.
 *
 * Inside the app the whole album travels along in the navigation params, which is why
 * these screens used to read `route.params` directly. From a URL only the id in the
 * address bar survives, and the album is in the store anyway (loaded at startup), so
 * look it up there first -- the copy in the params can be a screen or two old.
 */
export function useRootGalleryFromRoute(params: RootGalleryHolderScreenParams | undefined): ApiGallery | null {
	const galleryId = asId(params?.galleryId ?? params?.id);
	const fromStore = useGalleryStore((state) =>
		galleryId === undefined ? undefined : state.allGalleries.find((gallery) => String(gallery.id) === galleryId),
	);
	const handedOver = params?.children ? (params as ApiGallery) : undefined;

	return fromStore ?? handedOver ?? null;
}

/**
 * One album page inside an album, for `/fotos/<album>/<pagina>`.
 *
 * The URL carries both ids because the pages hang under the album; a page id on its own
 * would need a search through every album.
 */
export function useRootGalleryPageFromRoute(params: RootGalleryScreenParams | undefined): GalleryPage | null {
	const pageId = asId(params?.pageId ?? params?.id);
	const gallery = useRootGalleryFromRoute(
		params?.galleryId === undefined ? undefined : ({ galleryId: params.galleryId } as RootGalleryHolderScreenParams),
	);
	const fromStore = pageId === undefined ? undefined : gallery?.children.find((page) => String(page.id) === pageId);
	const handedOver = params?.type === 'Gallery' ? (params as GalleryPage) : undefined;

	return fromStore ?? handedOver ?? null;
}
